import Head from 'next/head';
import React, { FC, useMemo } from 'react';
import { Recipe } from '../../types/api/entities/recipe';
import { RecipeFilter } from '../../types/api/entities/recipe-filter';
import { Store } from '../../types/api/entities/store';
import { getMainImage } from '../../util/image';

interface Props {
  recipe: Recipe;
  filters: (RecipeFilter | undefined)[];
  store: Store;
}

const toDuration = (input: number): string => {
  const h = Math.floor(input / 3600);
  const m = Math.floor((input % 3600) / 60);
  const s = Math.floor(input % 60);
  return `PT${h}H${m}M${s}S`;
};

export const withRecipeJsonLd = function <P extends Props>(
  Component: React.ComponentType<P>,
): FC<P> {
  return (props) => {
    const { recipe, filters, store } = props;

    const ld = useMemo(() => {
      const image = getMainImage(recipe.media);

      const result: any = {
        '@context': 'https://schema.org/',
        '@type': 'Recipe',
        name: recipe.name,
        image: image
          ? [
              ...Object.values(image.urls['1x1']),
              ...Object.values(image.urls['4x3']),
              ...Object.values(image.urls['16x9']),
            ]
          : [],
        author: {
          '@type': 'Person',
          name: recipe.creator.name,
        },
        description: recipe.description,
        keywords: recipe.keywords.join(', '),
        recipeIngredient: recipe.ingredients,
        recipeInstructions: recipe.instructions.map((instruction) => ({
          '@type': 'HowToStep',
          text: instruction,
        })),
      };

      filters.forEach((filter) => {
        if (!filter) {
          return;
        }
        if (!result[filter.schemaType]) {
          result[filter.schemaType] = [];
        }

        result[filter.schemaType].push(...filter.options.map((option) => option.name));
      });

      if (store.theme?.deactivateRatings !== true) {
        result.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: recipe.rating,
          ratingCount: recipe.ratingCount,
        };
      }

      if (recipe.cookSeconds) {
        result.cookTime = toDuration(recipe.cookSeconds);
      }

      if (recipe.prepSeconds) {
        result.prepTime = toDuration(recipe.prepSeconds);
      }

      if (recipe.prepSeconds && recipe.cookSeconds) {
        result.totalTime = toDuration(recipe.cookSeconds + recipe.prepSeconds);
      }

      return result;
    }, [recipe, filters, store.theme?.deactivateRatings]);

    return (
      <>
        <Head>
          <script
            key={`recipe-${recipe.id}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
          />
        </Head>

        <Component {...props} />
      </>
    );
  };
};
